/**
 * Utility functions for handling API errors.
 */

import { HttpErrorResponse } from '@angular/common/http';

/**
 * Extract error message from various error formats.
 * Handles Firebase Functions errors, Angular HttpErrorResponse, and generic errors.
 */
type ErrorPayload = {
  error?: {
    message?: string;
    status?: string | number;
    code?: string;
  };
  message?: string;
  status?: string | number;
};

type ErrorLike =
  | {
      error?: ErrorPayload | string;
      message?: string;
      status?: number;
    }
  | string
  | null
  | undefined;

export function extractErrorMessage(error: unknown): string {
  if (!error) {
    return 'An unknown error occurred';
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof HttpErrorResponse) {
    // Status 0: no HTTP response (offline, CORS block, mixed content, browser extension, etc.)
    if (error.status === 0) {
      return 'Could not reach the server. Check your network, VPN, or firewall, or try again in a moment.';
    }

    const body = error.error;
    if (typeof body === 'object' && body !== null) {
      const o = body as ErrorPayload & { message?: string };
      const fromNested = o?.error?.message || o?.message;
      if (typeof fromNested === 'string' && fromNested.trim()) {
        return fromNested;
      }
    }
    if (typeof body === 'string' && body.trim()) {
      return body;
    }

    if (error.status >= 400) {
      const st = error.statusText?.trim();
      if (st && st !== 'Unknown Error') {
        return `${error.status} ${st}`;
      }
      return `Request failed (HTTP ${error.status}).`;
    }

    if (typeof error.message === 'string' && error.message.trim()) {
      return error.message;
    }
    return 'Request failed.';
  }

  if (typeof error !== 'object' || error === null) {
    return 'An unknown error occurred';
  }

  const err = error as {
    error?: ErrorPayload | string;
    message?: string;
    status?: number;
  };

  const nested = typeof err.error === 'string' ? { message: err.error } : err.error;

  // Try various error message paths (non-HTTP errors, e.g. Firebase client shapes)
  const message =
    (typeof nested === 'object' &&
      nested !== null &&
      nested.error?.message) ||
    (typeof nested === 'object' && nested !== null && nested.message) ||
    err.message ||
    (typeof nested === 'object' && nested !== null && nested.error?.status) ||
    (typeof nested === 'object' && nested !== null && nested.status) ||
    'An unknown error occurred';

  return typeof message === 'string' ? message : 'An unknown error occurred';
}

/**
 * Check if error indicates tenant suspension.
 * Checks both error message content and HTTP status code (403 Forbidden).
 */
export function isTenantSuspendedError(error: unknown): boolean {
  const err = error as ErrorLike;
  const status =
    typeof err === 'object' && err !== null && 'status' in err
      ? (err as { status?: number }).status
      : undefined;
  // Check HTTP status code first (403 Forbidden)
  if (status === 403) {
    const message = extractErrorMessage(err).toLowerCase();
    // If it's a 403, check if message indicates suspension
    if (
      message.includes('suspended') ||
      message.includes('tenant account is suspended') ||
      message.includes('account is suspended')
    ) {
      return true;
    }
    // 403 with permission denied could also be suspension
    if (message.includes('permission denied') || message.includes('not active')) {
      return true;
    }
  }

  // Check error message content
  const message = extractErrorMessage(err).toLowerCase();
  return (
    message.includes('suspended') ||
    message.includes('tenant account is suspended') ||
    message.includes('account is suspended')
  );
}

/**
 * Check if error indicates tenant cancellation.
 * Checks both error message content and HTTP status code (403 Forbidden).
 */
export function isTenantCancelledError(error: unknown): boolean {
  const err = error as ErrorLike;
  const status =
    typeof err === 'object' && err !== null && 'status' in err
      ? (err as { status?: number }).status
      : undefined;
  // Check HTTP status code first (403 Forbidden)
  if (status === 403) {
    const message = extractErrorMessage(err).toLowerCase();
    if (
      message.includes('cancelled') ||
      message.includes('tenant account is cancelled') ||
      message.includes('account is cancelled')
    ) {
      return true;
    }
  }

  // Check error message content
  const message = extractErrorMessage(err).toLowerCase();
  return (
    message.includes('cancelled') ||
    message.includes('tenant account is cancelled') ||
    message.includes('account is cancelled')
  );
}

/**
 * Get user-friendly error message for tenant status errors.
 */
export function getTenantStatusErrorMessage(error: unknown): string {
  const err = error as ErrorLike;
  if (isTenantSuspendedError(err)) {
    return 'Your tenant account is suspended. Please contact support for assistance.';
  }
  if (isTenantCancelledError(err)) {
    return 'Your tenant account has been cancelled. Please contact support for assistance.';
  }
  return extractErrorMessage(err);
}
